// One-time onboarding "coach mark" visibility, persisted per device.
//
// Uses Capacitor Preferences (which falls back to localStorage on web), the
// same storage the app already uses for auth - so a mark shows once per device
// and never again, surviving app restarts.
import { useCallback, useEffect, useState } from 'react';
import { Preferences } from '@capacitor/preferences';

const keyFor = (id: string) => `coachmark_seen_${id}`;

/**
 * Returns `{ show, dismiss }` for a one-time coach mark.
 *
 * - `active` gates it: the mark is only ever shown while the triggering
 *   condition holds (e.g. the tray is open, the profile is on the Game Log
 *   tab). When `active` goes false the mark hides without being marked seen,
 *   so an interrupted first run can still show it next time.
 * - `dismiss()` hides it AND records it as seen, so it never shows again.
 */
export function useCoachMark(id: string, active: boolean) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!active) {
      setShow(false);
      return;
    }
    let cancelled = false;
    Preferences.get({ key: keyFor(id) })
      .then(({ value }) => {
        if (!cancelled && value !== 'true') setShow(true);
      })
      .catch(() => {
        /* if storage is unavailable, just don't nag the user */
      });
    return () => {
      cancelled = true;
    };
  }, [id, active]);

  const dismiss = useCallback(() => {
    setShow(false);
    Preferences.set({ key: keyFor(id), value: 'true' }).catch(() => {});
  }, [id]);

  return { show, dismiss };
}
